/**
 * S9b — Multileader-side mesh + text-renderable factories.
 *
 * The multileader adapter (`multileaderFromEntity` / `multileaderToPrimitives`
 * → shared `multileaderGeometry.ts`) emits a `MultileaderPrimitive[]` under
 * `geometry.primitives`. They render across four seams, mirroring the
 * DIMENSION split:
 *
 *   * `leader-line` (straight / spline) / `dogleg` / `landing` → the
 *     BatchedLayer thick-line pipeline via `geometryToLineVertices`
 *     (`batchedLayer.ts` case `"multileader"`).
 *   * `arrow` heads → {@link buildMultileaderArrowMeshes} — one
 *     `THREE.InstancedMesh` / `THREE.LineSegments` per `(variant, colorHex)`
 *     group via the shared `buildArrowInstanceMeshes` (same materialiser the
 *     dimension arrow factory uses).
 *   * `mtext-content` → {@link synthesizeMultileaderTextRenderables} — one
 *     synthetic `RenderableEntity` with `geometry.kind = "mtext"` per content
 *     primitive, pushed through the same MTEXT SDF / SHX pipeline real MTEXT
 *     entities use.
 *   * `block-content` → NOT handled here: its `flattened` `RenderableEntity[]`
 *     is expanded into first-class child renderables at bulk-load (identical
 *     to a top-level INSERT walk — see the `block-content.flattened` contract
 *     in `types.ts` and `finishSceneBulkLoad`), so each block child keeps its
 *     own color / linetype / kind through the normal per-kind pipelines.
 *
 * # CLAUDE.md ПРИОРИТЕТ #1
 *
 * No simplification: the arrow materialiser honours every `(variant, colorHex)`
 * group and the filled-InstancedMesh / outline-LineSegments split; the MTEXT
 * content is synthesised as a real `mtext` renderable (runs preserved), not a
 * flattened single-line `text` placeholder.
 */

import type { MTextRun, RenderableEntity } from "../types";
import { directMeshLayerStamp } from "../layerChainVisibility";
import {
  buildArrowInstanceMeshes,
  type ArrowDecoratorObject3D,
  type ArrowInstanceInput,
} from "../dimensions/arrowInstanceMesh";

/* ── Arrow heads ─────────────────────────────────────────────────── */

/**
 * Walk a multileader renderable's primitives and emit its arrow-head
 * decorator meshes (batched InstancedMesh for filled prototypes, per-instance
 * LineSegments for outline prototypes). Returns `[]` when the multileader has
 * no drawable arrow (e.g. arrowhead `_NONE`).
 */
export function buildMultileaderArrowMeshes(
  renderable: RenderableEntity,
): ArrowDecoratorObject3D[] {
  if (renderable.geometry.kind !== "multileader") return [];
  // The `arrow` primitive is a structural superset of `ArrowInstanceInput`
  // (same tip / direction / size / variant / colorHex, plus a `kind` tag), so
  // it is pushed directly.
  const instances: ArrowInstanceInput[] = [];
  for (const prim of renderable.geometry.primitives) {
    if (prim.kind === "arrow") instances.push(prim);
  }
  // Codex S9b round-5 P2 (sibling of the dimension-arrow fix) — a nested
  // MLEADER emitted as another MLEADER's block-content child carries a
  // `renderParentId` and its synthetic id is absent from the spatial index;
  // stamp the PARENT id so `applySceneVisibilityMask` keeps the arrows with
  // the parent. No-op for a top-level MLEADER (`renderParentId` absent → the
  // real id), keeping this one-truth with the hatch / text / dimension
  // factories (`renderParentId ?? id`).
  return buildArrowInstanceMeshes(
    instances,
    renderable.renderParentId ?? renderable.id,
    "multileader-arrow",
    directMeshLayerStamp(renderable),
  );
}

/* ── MTEXT content synthesis ─────────────────────────────────────── */

/**
 * Conservative bbox half-extent factor for synthesised MTEXT content — the
 * MTEXT layout computes per-glyph bounds itself; we only need a non-zero box
 * so dispose / registry pipelines don't treat the entry as degenerate.
 */
const TEXT_BBOX_HALF_FACTOR = 0.3;

/** Total visible glyph count across the MTEXT runs (paragraph breaks aside). */
function totalRunLength(runs: readonly MTextRun[]): number {
  let len = 0;
  for (const run of runs) len += run.text.length;
  return len;
}

/**
 * Walk a multileader renderable's primitives, emit one synthetic
 * `RenderableEntity` (`geometry.kind = "mtext"`) per `mtext-content` primitive
 * so the MLEADER label flows through the same MTEXT text pipeline (SDF / SHX)
 * real MTEXT entities use.
 *
 * Id namespacing: `${renderable.id}::mtext::${i}` — the `::` separator matches
 * the dimension text-synthesis convention and keeps synthesised ids distinct
 * from any real entity id (real ids never contain `::`).
 */
export function synthesizeMultileaderTextRenderables(
  renderable: RenderableEntity,
): RenderableEntity[] {
  if (renderable.geometry.kind !== "multileader") return [];

  const out: RenderableEntity[] = [];
  let i = 0;
  for (const prim of renderable.geometry.primitives) {
    if (prim.kind !== "mtext-content") continue;
    const halfX = Math.max(
      totalRunLength(prim.runs) * prim.height * TEXT_BBOX_HALF_FACTOR,
      prim.height,
    );
    const halfY = prim.height;
    out.push({
      id: `${renderable.id}::mtext::${i}`,
      // Codex S9b P2 — attribute the synthetic label to the REAL MLEADER so the
      // viewport-visibility mask + base layout pre-hide (both keyed on
      // spatial-index membership) treat it as the parent, not an unknown id.
      // Codex round-6 P2 ("Preserve parent attribution for nested MLEADER
      // text"): a nested MLEADER emitted as another MLEADER's block-content
      // child already carries `renderParentId` = the top-level callout (a
      // spatial-index entity); overwriting it with the nested child id (absent
      // from the index) would make the mask drop the label. Preserve the
      // existing parent — mirrors the arrow-decorator fix.
      renderParentId: renderable.renderParentId ?? renderable.id,
      // Round 3 P1 — an MLEADER that is itself a block child carries its
      // INSERT's layer chain; the synthesised label inherits it.
      ...(renderable.renderParentLayers
        ? { renderParentLayers: renderable.renderParentLayers }
        : {}),
      spaceId: renderable.spaceId,
      type: renderable.type,
      bounds: {
        minX: prim.anchor.x - halfX,
        minY: prim.anchor.y - halfY,
        maxX: prim.anchor.x + halfX,
        maxY: prim.anchor.y + halfY,
      },
      layer: renderable.layer,
      colorHex: prim.colorHex,
      lineweightWorld: renderable.lineweightWorld,
      // Synthesised MLEADER text inherits the parent's linetype (text buckets
      // are continuous regardless, but keep the field faithful).
      linetype: renderable.linetype,
      zOrder: renderable.zOrder,
      geometry: {
        kind: "mtext",
        position: prim.anchor,
        rotation: prim.rotation,
        height: prim.height,
        widthFactor: 1,
        styleName: prim.styleName,
        runs: prim.runs,
        lineSpacingFactor: prim.lineSpacingFactor,
        attachmentPoint: prim.attachmentPoint,
        // GitHub Codex review PR #558 (post-rebase fresh pass) P2 —
        // handedness companion to `rotation` above, sourced from
        // `MultileaderEntity.textMirrored`. Consumed by the SAME
        // `sceneTextMeshFactories.ts` `applyMirrorScale` helper real MTEXT's
        // own `mirrored` flag already drives (both the SDF/TTF
        // `buildTextEntityMesh` and SHX `buildShxTextEntityMesh` paths) — no
        // new render pipeline, just a new source for an existing sink.
        mirrored: prim.mirrored,
      },
    });
    i += 1;
  }
  return out;
}
